import { ReactNode, useState } from 'react';
import { Box, Link, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import useResponsive from 'src/hooks/useResponsive';
import LoginHeader from 'src/layouts/login/LoginHeader';
import { PATH_AUTH } from 'src/routes/paths';
import OwnerLoginWelcome from './OwnerLoginWelcome';
import {
  LoginFormColumn,
  LoginFormSurface,
  LoginMain,
  LoginPageRoot,
} from './styles';

interface OwnerLoginLayoutProps {
  children: ReactNode;
  showRegistration: boolean;
}

export default function OwnerLoginLayout({
  children,
  showRegistration,
}: OwnerLoginLayoutProps): JSX.Element {
  const { t } = useTranslation();
  const isDesktop = useResponsive('up', 'lg');
  const [isArtworkReady, setIsArtworkReady] = useState(false);
  const [hasFormInteraction, setHasFormInteraction] = useState(false);

  return (
    <LoginPageRoot data-testid="owner-login-page">
      <LoginHeader />
      <LoginMain
        sx={{
          '--welcome-animation-play-state':
            !isDesktop || isArtworkReady ? 'running' : 'paused',
        }}
      >
        {isDesktop && (
          <OwnerLoginWelcome onArtworkReady={() => setIsArtworkReady(true)} />
        )}
        <LoginFormColumn aria-labelledby="owner-login-title">
          <LoginFormSurface
            data-testid="owner-login-form"
            onFocusCapture={() => setHasFormInteraction(true)}
            sx={hasFormInteraction ? { animation: 'none' } : undefined}
          >
            <Typography
              component="h1"
              variant="h4"
              id="owner-login-title"
              sx={{ fontWeight: 800, lineHeight: 1.3 }}
            >
              {t('ownerLogin.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('ownerLogin.description')}
            </Typography>
            <Box sx={{ mt: 3, '& .MuiLink-root': { color: 'text.primary' } }}>
              {children}
            </Box>
            {showRegistration && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 3, textAlign: 'center' }}
              >
                {t('auth.noAccount')}{' '}
                <Link
                  component={RouterLink}
                  to={PATH_AUTH.register}
                  color="text.primary"
                  sx={{ fontWeight: 700 }}
                >
                  {t('auth.registerLink')}
                </Link>
              </Typography>
            )}
          </LoginFormSurface>
          <Stack spacing={0.5} sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {t('ownerLogin.professionalPrompt')}
            </Typography>
            <Link
              component={RouterLink}
              to={PATH_AUTH.professionalLogin}
              variant="body2"
              color="text.primary"
              sx={{ fontWeight: 600 }}
            >
              {t('ownerLogin.professionalLink')}
            </Link>
          </Stack>
        </LoginFormColumn>
      </LoginMain>
    </LoginPageRoot>
  );
}
